/**
 * Centralized query key factory for React Query
 * Ensures consistent key generation and proper deduplication
 */

export const queryKeys = {
	// Canonical cards
	canonicalCards: {
		all: ['canonical-cards'] as const,
		infinite: (filters: Record<string, any>) =>
			['canonical-cards-infinite', sortObjectKeys(filters)] as const,
		browse: (filters: Record<string, any>) =>
			['canonical-cards-browse', sortObjectKeys(filters)] as const,
		byId: (id: string) => ['canonical-card', id] as const,
		printings: (id: string, setFilter?: string[], rarityFilter?: string[]) =>
			['canonical-card-printings', id, sorted(setFilter), sorted(rarityFilter)] as const
	},

	// Printings
	printings: {
		all: ['printings'] as const,
		batch: (ids: string[], setFilter?: string[], rarityFilter?: string[]) =>
			['printings-batch', sorted(ids), sorted(setFilter), sorted(rarityFilter)] as const,
		byId: (id: string) => ['printing', id] as const
	},

	// Collection
	collection: {
		all: ['collection'] as const,
		enrichment: (visibleIds: string[]) =>
			['collection-enrichment-batch', sorted(visibleIds)] as const,
		userCollection: (userId: string) => ['user-collection', userId] as const
	},

	// Legality
	legality: {
		all: ['legality'] as const,
		batch: (canonicalIds: string[]) => ['batch-legality', sorted(canonicalIds)] as const,
		byCard: (setId?: string, canonicalId?: string) => ['card-legality', setId, canonicalId] as const
	},

	// Format and game data
	formats: {
		all: ['formats'] as const,
		legalCards: (formatCode: string) => ['legal-canonical-cards', formatCode] as const,
		coreDateRange: ['core-date-range'] as const
	},

	// Sets
	sets: {
		all: ['sets'] as const,
		byCode: (code: string) => ['set', code] as const
	},

	// Decks
	decks: {
		all: ['decks'] as const,
		byId: (id: string) => ['deck', id] as const,
		byUser: (userId: string) => ['user-decks', userId] as const,
		stats: (deckId: string) => ['deck-stats', deckId] as const,
		versions: (deckId: string) => ['deck-versions', deckId] as const
	},

	// Portfolio
	portfolio: {
		all: ['portfolio'] as const,
		trends: (userId: string) => ['portfolio-trends', userId] as const,
		history: (userId: string, days?: number) =>
			['portfolio-history', userId, days ?? 'all'] as const
	},

	// Sealed Products
	sealedProducts: {
		all: ['sealed-products'] as const,
		bySet: (setId: string) => ['sealed-products', 'set', setId] as const,
		byId: (id: string) => ['sealed-product', id] as const
	},

	sealedCollection: {
		user: (userId: string) => ['sealed-collection', userId] as const
	},

	// Game stores
	gameStores: {
		all: ['game-store'] as const,
		byId: (storeId: string) => ['game-store', storeId] as const,
		upcomingShows: (storeId: string) => ['game-store', storeId, 'shows'] as const
	},

	// user_items (owner inventory + buyer-facing item detail)
	userItems: {
		all: ['user-item'] as const,
		byId: (idOrShortId: string | undefined) => ['user-item', idOrShortId] as const,
		mineAll: ['my-items'] as const,
		mine: (userId: string | undefined) => ['my-items', userId] as const,
		collection: (userId: string | undefined, visibility: string) =>
			['user-collection', userId, visibility] as const
	},

	// Legacy marketplace-items key (broad invalidation target only; the
	// underlying hook was removed but downstream mutations still fan out
	// to it defensively).
	marketplaceItems: {
		all: ['marketplace-items'] as const
	},

	// eBay
	ebay: {
		listings: (userId: string | undefined) => ['my-ebay-listings', userId] as const,
		listingsAll: ['my-ebay-listings'] as const,
		activeCount: (userId: string | undefined) =>
			['my-ebay-listings-active-count', userId] as const,
		orders: (userId: string | undefined) => ['ebay-orders', userId] as const,
		ordersAll: ['ebay-orders'] as const,
		templates: (userId: string | undefined) => ['ebay-templates', userId] as const,
		templatesAll: ['ebay-templates'] as const,
		category: (itemId: string) => ['ebay-category', itemId] as const
	},

	// Reviews
	reviews: {
		byUser: (userId: string | undefined) => ['user-reviews', userId] as const,
		tagCounts: (userId: string | undefined) => ['review-tag-counts', userId] as const,
		weighted: (userId: string | undefined) => ['weighted-rating', userId] as const
	},

	// Admin
	admin: {
		users: (search: string, filter: string, limit: number) =>
			['admin-users', search, filter, limit] as const,
		usersAll: ['admin-users'] as const,
		reports: (status?: string) => ['admin-reports', status] as const,
		reportsAll: ['admin-reports'] as const,
		pendingReportsCount: ['pending-reports-count'] as const
	},

	// Inquiries
	inquiries: {
		all: ['inquiries'] as const,
		byUser: (userId: string | undefined) => ['inquiries', userId] as const,
		unread: ['inquiries-unread'] as const,
		unreadByUser: (userId: string | undefined) => ['inquiries-unread', userId] as const,
		replies: (inquiryId: string, table: string, userId?: string) =>
			['inquiry-replies', inquiryId, table, userId] as const,
		repliesFor: (inquiryId: string, table: string) =>
			['inquiry-replies', inquiryId, table] as const
	}
} as const;


/**
 * Sort array for consistent query keys
 */
function sorted<T>(arr: T[] | undefined): string {
	if (!arr || arr.length === 0) return '';
	return [...arr].sort().join(',');
}

/**
 * Sort object keys alphabetically for consistent stringification
 */
function sortObjectKeys(obj: Record<string, any>): string {
	const sorted: Record<string, any> = {};
	Object.keys(obj)
		.sort()
		.forEach((key) => {
			const value = obj[key];
			// Sort arrays within the object
			if (Array.isArray(value)) {
				sorted[key] = [...value].sort();
			} else {
				sorted[key] = value;
			}
		});
	return JSON.stringify(sorted);
}
